import React from "react";

import Header from "../components/Header";
import Footer from "../components/Footer";

export default function Terms() {
  return (
    <>
      <Header />

      <div className="max-w-4xl mx-auto px-6 py-16 text-gray-800">
      <h1 className="text-3xl font-bold mb-2">Terms & Conditions</h1>
      <p className="text-sm text-gray-500 mb-8">
        Last updated: January 2026
      </p>

      <p className="mb-6">
        These Terms & Conditions ("Terms") govern your access to and use of the
        GymPlix platform, accessible at{" "}
        <a
          href="https://app.gymplix.com"
          className="underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://app.gymplix.com
        </a>{" "}
        (the "Service").
        <br />
        <strong>GymPlix is operated by Creative Brains LTD.</strong>
      </p>

      <p className="mb-6">
        By accessing or using GymPlix, you agree to be bound by these Terms. If
        you do not agree, you may not use the Service.
      </p>

      {/* 1 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        1. Description of the Service
      </h2>

      <p className="mb-6">
        GymPlix is a content management and publishing platform designed to help
        users prepare, organize, and publish content to third-party platforms
        such as Meta Platforms (Facebook) and Pinterest using official, authorized APIs.
      </p>

      <p className="mb-6">
        The Service provides tools for social media content management, including:
      </p>

      <ul className="list-disc pl-6 mb-6">
        <li>Content preparation and organization tools</li>
        <li>Publishing management for social media platforms</li>
        <li>Analytics and performance tracking</li>
        <li>Integration with authorized third-party APIs</li>
      </ul>

      <p className="mb-6">
        <strong>GymPlix provides tools only. All actions performed through the platform
        are initiated by the user.</strong>
      </p>

      {/* 2 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        2. Eligibility
      </h2>

      <p className="mb-6">
        You must be at least 18 years old to use GymPlix. By using the Service,
        you represent that you meet this requirement and have the legal capacity
        to enter into these Terms.
      </p>

      {/* 3 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        3. User Accounts
      </h2>

      <ul className="list-disc pl-6 mb-6">
        <li>You are responsible for maintaining the confidentiality of your account.</li>
        <li>You are responsible for all activity that occurs under your account.</li>
        <li>You must provide accurate and complete information when registering.</li>
      </ul>

      <p className="mb-6">
        GymPlix is not responsible for unauthorized access caused by user
        negligence.
      </p>

      {/* 4 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        4. Third-Party Integrations
      </h2>

      <p className="mb-4">
        GymPlix allows you to connect third-party services such as Meta Platforms (Facebook),
        Pinterest, and external websites.
      </p>

      <ul className="list-disc pl-6 mb-6">
        <li><strong>All integrations require explicit user authorization.</strong></li>
        <li>You may revoke access at any time through the respective platform settings.</li>
        <li>GymPlix does not control third-party platforms.</li>
        <li>Use of third-party services is subject to their respective terms and policies.</li>
      </ul>

      {/* 5 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        5. Meta Platforms (Facebook) API Usage
      </h2>

      <p className="mb-4">
        GymPlix integrates with Meta Platforms (Facebook) using the official Facebook API.
      </p>

      <p className="mb-2">Regarding Meta Platforms (Facebook) integration:</p>

      <ul className="list-disc pl-6 mb-4">
        <li>Content is published only with explicit user permission</li>
        <li>GymPlix does not post content autonomously</li>
        <li>Users remain responsible for all published content</li>
        <li>All data access requires user authorization through Facebook's OAuth flow</li>
      </ul>

      <p className="mb-4">
        <strong>GymPlix complies with Meta's Platform Policies and developer requirements.</strong>
        We do not use Facebook data for advertising purposes or share data with third parties.
      </p>

      <p className="mb-6">
        Users must comply with Meta's terms of service when using Facebook features
        through GymPlix.
      </p>

      {/* 6 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        6. Pinterest API Usage
      </h2>

      <p className="mb-4">
        GymPlix integrates with Pinterest using the official Pinterest API.
      </p>

      <ul className="list-disc pl-6 mb-6">
        <li>Content is published only with user permission.</li>
        <li>GymPlix does not post content autonomously.</li>
        <li>Users remain responsible for all published content.</li>
      </ul>

      <p className="mb-6">
        GymPlix complies with Pinterest's developer policies and platform rules.
      </p>

      {/* 7 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        7. User Responsibilities
      </h2>

      <p className="mb-2">When using GymPlix, you agree to:</p>

      <ul className="list-disc pl-6 mb-6">
        <li>Provide accurate and truthful information</li>
        <li>Maintain the security of your account credentials</li>
        <li>Use the Service only for lawful purposes</li>
        <li>Respect the terms of service of connected third-party platforms</li>
        <li>Not attempt to gain unauthorized access to any systems or data</li>
        <li>Not use the Service to violate any applicable laws or regulations</li>
        <li>Not infringe upon the intellectual property rights of others</li>
        <li>Not upload malicious, harmful, or illegal content</li>
        <li>Not abuse or misuse third-party APIs</li>
      </ul>

      {/* 8 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        8. User Content
      </h2>

      <p className="mb-4">
        You retain ownership of all content you upload, generate, or publish
        using GymPlix.
      </p>

      <p className="mb-6">
        By using the Service, you grant GymPlix a limited, non-exclusive license
        to store and process content solely for the purpose of providing the
        Service.
      </p>

      {/* 9 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        9. Prohibited Uses
      </h2>

      <p className="mb-2">You agree not to use GymPlix to:</p>

      <ul className="list-disc pl-6 mb-6">
        <li>Violate any applicable laws or regulations</li>
        <li>Infringe intellectual property rights</li>
        <li>Upload malicious or harmful content</li>
        <li>Abuse or misuse third-party APIs</li>
        <li>Attempt to interfere with or disrupt the Service</li>
        <li>Use the Service for fraudulent purposes</li>
        <li>Harass, abuse, or harm other users</li>
      </ul>

      {/* 10 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        10. Data & Privacy
      </h2>

      <p className="mb-6">
        Use of GymPlix is also governed by our Privacy Policy, which explains how
        we collect and process personal data. By using the Service, you consent to
        the collection and use of your data as described in our Privacy Policy.
      </p>

      {/* 11 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        11. Service Availability
      </h2>

      <p className="mb-6">
        GymPlix is provided on an "as is" and "as available" basis. We do not
        guarantee uninterrupted or error-free operation. We reserve the right to
        modify, suspend, or discontinue the Service at any time.
      </p>

      {/* 12 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        12. Termination
      </h2>

      <p className="mb-2"><strong>User-Initiated Termination:</strong></p>
      <ul className="list-disc pl-6 mb-4">
        <li>You may terminate your account at any time by contacting us</li>
        <li>Upon termination, your right to use the Service ceases immediately</li>
      </ul>

      <p className="mb-2"><strong>Service-Initiated Termination:</strong></p>
      <ul className="list-disc pl-6 mb-4">
        <li>We reserve the right to suspend or terminate access to GymPlix if these
        Terms are violated.</li>
        <li>We may terminate or suspend access for behavior that harms other users,
        our business, or third parties.</li>
        <li>We may terminate accounts that are inactive for an extended period.</li>
      </ul>

      <p className="mb-6">
        Upon termination, we will delete your personal data within 30 days in
        accordance with our Data Deletion policy, except where retention is required
        by law.
      </p>

      {/* 13 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        13. Limitation of Liability
      </h2>

      <p className="mb-6">
        To the maximum extent permitted by law, GymPlix shall not be liable for
        any indirect, incidental, or consequential damages arising from use of
        the Service. This includes, but is not limited to, loss of profits, data,
        or business opportunities.
      </p>

      {/* 14 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        14. Disclaimers
      </h2>

      <p className="mb-6">
        GymPlix makes no warranties, express or implied, regarding the reliability,
        accuracy, or availability of the Service. You use the Service at your
        own risk.
      </p>

      {/* 15 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        15. Changes to These Terms
      </h2>

      <p className="mb-6">
        We may update these Terms from time to time. Continued use of the Service
        constitutes acceptance of the updated Terms. We will notify users of
        material changes to these Terms.
      </p>

      {/* 16 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        16. Governing Law
      </h2>

      <p className="mb-6">
        These Terms shall be governed by and construed in accordance with applicable
        laws, without regard to conflict of law principles.
      </p>

      {/* 17 */}
      <h2 className="text-xl font-semibold mt-10 mb-3">
        17. Contact Information
      </h2>

      <p>
        For questions regarding these Terms, please contact:
        <br />
        <strong>GymPlix</strong> — Operated by Creative Brains LTD
        <br />
        📧{" "}
        <a
          href="mailto:support@gymplix.com"
          className="underline"
        >
          support@gymplix.com
        </a>
      </p>

   </div>
         <Footer />

    </>
  );
}
